import { useEffect, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { ArrowDown, ArrowRight, MapPin } from 'lucide-react'
import { useLenis } from 'lenis/react'
import { profile, skills, socials } from '@/data/site'
import { ButtonLink } from '@/components/ui/button'
import { Marquee } from '@/components/ui/marquee'
import { socialIcon } from '@/components/ui/icons'

const rotating = ['3D scenes', 'muscle signals', 'medical images', 'network intent']

const ease = [0.22, 1, 0.36, 1] as const

const fadeUp = (delay: number) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay, ease },
})

/**
 * Hero framed around the fixed background ghost (see GhostBackdrop).
 *  - lg+: three columns — name | empty centre column | details. The container
 *    is horizontally centred, so the centre column always lines up with the
 *    ghost regardless of viewport height.
 *  - < lg: stacked — name, an empty "stage" row whose centre sits at 50svh
 *    (where the ghost is), then details.
 */
export function Hero() {
  const lenis = useLenis()
  const reduce = useReducedMotion()
  const [i, setI] = useState(0)

  useEffect(() => {
    if (reduce) return
    const t = setInterval(() => setI((v) => (v + 1) % rotating.length), 2600)
    return () => clearInterval(t)
  }, [reduce])

  const scrollTo = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    if (!lenis) return
    e.preventDefault()
    lenis.scrollTo(id, { offset: -88, duration: 1.2 })
  }

  return (
    <section id="top" aria-labelledby="hero-title" className="relative">
      <div className="container grid min-h-[100svh] content-start grid-rows-[minmax(calc(50svh-120px),auto)_240px_auto] md:grid-rows-[minmax(calc(50svh-160px),auto)_320px_auto] lg:grid-cols-[1fr_300px_1fr] lg:grid-rows-1 lg:content-stretch lg:items-center">
        {/* ── Name (left of / above the ghost) ─────────────────────────── */}
        <div className="flex flex-col items-center justify-end pt-20 text-center md:pt-24 lg:items-end lg:justify-center lg:pt-0 lg:text-right">
          <motion.div
            {...fadeUp(0)}
            className="mb-5 inline-flex items-center gap-2.5 rounded-full md:mb-7 border border-white/10 bg-ink/50 py-1.5 pl-2 pr-4 text-xs text-zinc-300 backdrop-blur"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            {profile.lookingFor}
          </motion.div>

          <h1 id="hero-title">
            <motion.span
              initial={{ opacity: 0, y: 24, filter: 'blur(10px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              transition={{ duration: 0.9, delay: 0.1, ease }}
              className="block text-[clamp(3rem,9vw,4.5rem)] font-medium leading-[0.92] tracking-[-0.045em] text-white lg:text-[clamp(4.5rem,7vw,7rem)]"
            >
              <span className="lg:block">Pujari</span>{' '}
              <span className="text-gradient font-serif font-normal italic tracking-[-0.02em] lg:block">Keerthika</span>
            </motion.span>
            <motion.span
              {...fadeUp(0.3)}
              className="mt-5 block font-mono text-xs uppercase tracking-[0.28em] text-zinc-400"
            >
              {profile.role}
            </motion.span>
          </h1>
        </div>

        {/* ── Stage: the fixed ghost shows through here ───────────────── */}
        <div aria-hidden="true" className="h-full" />

        {/* ── Details (right of / below the ghost) ────────────────────── */}
        <div className="flex flex-col items-center pb-20 text-center lg:items-start lg:pb-0 lg:text-left">
          <motion.p
            {...fadeUp(0.45)}
            className="flex flex-col items-center text-lg text-zinc-400 lg:items-start"
          >
            <span>Teaching machines to understand</span>
            <span className="relative mt-1 flex h-[1.25em] overflow-hidden text-4xl md:text-5xl">
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={rotating[i]}
                  initial={{ y: '100%', opacity: 0 }}
                  animate={{ y: '0%', opacity: 1 }}
                  exit={{ y: '-100%', opacity: 0 }}
                  transition={{ duration: 0.45, ease }}
                  className="block whitespace-nowrap font-serif italic leading-[1.15] text-white"
                >
                  {rotating[i]}
                </motion.span>
              </AnimatePresence>
            </span>
          </motion.p>

          <motion.p {...fadeUp(0.55)} className="mt-5 max-w-md text-pretty leading-relaxed text-zinc-400">
            {profile.tagline}
          </motion.p>

          <motion.div {...fadeUp(0.65)} className="mt-8 flex flex-wrap items-center justify-center gap-3 lg:justify-start">
            <ButtonLink href="#publications" size="lg" onClick={(e) => scrollTo(e, '#publications')}>
              Read my research
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </ButtonLink>
            <ButtonLink href="#contact" size="lg" variant="secondary" onClick={(e) => scrollTo(e, '#contact')}>
              Get in touch
            </ButtonLink>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 lg:-ml-2.5 lg:justify-start"
          >
            <span className="flex items-center">
              {socials.map((s) => {
                const Icon = socialIcon[s.name]
                return (
                  <a
                    key={s.name}
                    href={s.url}
                    target="_blank"
                    rel="me noopener noreferrer"
                    aria-label={`${profile.name} on ${s.name}`}
                    className="grid h-10 w-10 place-items-center rounded-full text-zinc-400 transition hover:bg-white/[0.06] hover:text-white"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                )
              })}
            </span>
            <span className="flex items-center gap-2 font-mono text-xs text-zinc-500">
              <MapPin className="h-3.5 w-3.5" /> {profile.location}
            </span>
          </motion.div>
        </div>
      </div>

      <a
        href="#about"
        onClick={(e) => scrollTo(e, '#about')}
        className="absolute bottom-24 left-1/2 hidden -translate-x-1/2 items-center gap-2 text-xs text-zinc-500 transition-colors hover:text-zinc-300 lg:flex"
      >
        <ArrowDown className="h-3.5 w-3.5 animate-bounce" /> Scroll
      </a>

      <div className="pb-6">
        <Marquee duration="50s">
          {skills.map((s) => (
            <span
              key={s}
              className="whitespace-nowrap rounded-full border border-white/[0.07] bg-ink/40 px-4 py-2 font-mono text-xs text-zinc-400 backdrop-blur"
            >
              {s}
            </span>
          ))}
        </Marquee>
      </div>
    </section>
  )
}
